import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

type StatusKind = "idle" | "ok" | "ko" | "warn";

type LogMap = Record<string, string[]>;
type StatusMap = Record<string, StatusKind>;
type MessageMap = Record<string, string>;

let activeCameraStream: MediaStream | null = null;

function nowIso() {
    return new Date().toISOString();
}

function formatError(err: unknown): string {
    if (err instanceof Error) return `${err.name}: ${err.message}`;
    return String(err);
}

function pushLog(setLogs: React.Dispatch<React.SetStateAction<LogMap>>, key: string, msg: string) {
    setLogs(prev => ({
        ...prev,
        [key]: [`[${nowIso()}] ${msg}`, ...(prev[key] ?? [])],
    }));
}

function setResult(
    setStatuses: React.Dispatch<React.SetStateAction<StatusMap>>,
    setMessages: React.Dispatch<React.SetStateAction<MessageMap>>,
    key: string,
    status: StatusKind,
    message: string
) {
    setStatuses(prev => ({...prev, [key]: status}));
    setMessages(prev => ({...prev, [key]: message}));
}

function StatusPill({status}: { status: StatusKind }) {
    const map = {
        idle: {label: "—", bg: "#f4f4f4", border: "#ddd"},
        ok: {label: "OK", bg: "#ecfff0", border: "#a7e3b5"},
        ko: {label: "KO", bg: "#fff0f0", border: "#f3b1b1"},
        warn: {label: "WARN", bg: "#fff8e8", border: "#f0d08a"},
    };

    const s = map[status];

    return (
        <span
            style={{
                fontSize: 12,
                padding: "4px 8px",
                borderRadius: 999,
                border: `1px solid ${s.border}`,
                background: s.bg,
                display: "inline-flex",
                alignItems: "center",
                minWidth: 48,
                justifyContent: "center",
            }}
        >
      {s.label}
    </span>
    );
}

function Card({
                  title,
                  status,
                  message,
                  actions,
                  logs,
                  children,
              }: {
    title: string;
    status: StatusKind;
    message?: string;
    actions?: React.ReactNode;
    logs?: string[];
    children?: React.ReactNode;
}) {
    return (
        <section
            style={{
                border: "1px solid #e6e6e6",
                borderRadius: 12,
                padding: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,.04)",
                background: "#fff",
            }}
        >
            <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8}}>
                <div style={{fontWeight: 700}}>{title}</div>
                <StatusPill status={status}/>
            </div>

            {message && (
                <div style={{fontSize: 12, opacity: 0.9, marginBottom: 10, whiteSpace: "pre-line"}}>
                    {message}
                </div>
            )}

            {children}

            {actions && (
                <div style={{display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10}}>
                    {actions}
                </div>
            )}

            <pre
                style={{
                    marginTop: 10,
                    padding: 10,
                    background: "#fafafa",
                    border: "1px solid #eee",
                    borderRadius: 10,
                    overflow: "auto",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    minHeight: 72,
                }}
            >
        {(logs ?? []).join("\n")}
      </pre>
        </section>
    );
}

function ActionButton({
                          onClick,
                          children,
                      }: {
    onClick: () => void | Promise<void>;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={() => void onClick()}
            style={{
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #ddd",
                background: "#f6f6f6",
                cursor: "pointer",
            }}
        >
            {children}
        </button>
    );
}

export default function WebviewTestPage() {
    const [statuses, setStatuses] = useState<StatusMap>({});
    const [messages, setMessages] = useState<MessageMap>({});
    const [logs, setLogs] = useState<LogMap>({});
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [dragActive, setDragActive] = useState(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const filePickerTimerRef = useRef<number | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const userAgent = useMemo(() => navigator.userAgent, []);

    const log = useCallback((key: string, msg: string) => {
        pushLog(setLogs, key, msg);
    }, []);

    const setCardResult = useCallback((key: string, status: StatusKind, message: string) => {
        setResult(setStatuses, setMessages, key, status, message);
    }, []);

    const runEnvironmentTest = useCallback(() => {
        try {
            const https = location.protocol === "https:";
            const secure = window.isSecureContext;
            const inIframe = window.top !== window.self;
            const cookieEnabled = navigator.cookieEnabled;

            const summary = [
                `URL: ${location.href}`,
                `Origin: ${location.origin}`,
                `Protocol: ${location.protocol}`,
                `HTTPS: ${https}`,
                `Secure context: ${secure}`,
                `In iframe: ${inIframe}`,
                `navigator.cookieEnabled: ${cookieEnabled}`,
                `document.visibilityState: ${document.visibilityState}`,
                `User agent: ${navigator.userAgent}`,
            ].join("\n");

            const status: StatusKind = secure ? "ok" : "warn";
            const message =
                secure
                    ? "Contesto sicuro OK. Camera, clipboard e alcune API hanno più probabilità di funzionare."
                    : "Contesto non sicuro. Camera, clipboard e altre API potrebbero essere limitate o bloccate.";

            setCardResult("env", status, message);
            pushLog(setLogs, "env", summary);
        } catch (err) {
            setCardResult("env", "ko", "Errore durante il test environment.");
            log("env", formatError(err));
        }
    }, [log, setCardResult]);

    const runWindowTest = useCallback(() => {
        try {
            const lines = [
                `typeof window = ${typeof window}`,
                `typeof window.open = ${typeof window.open}`,
                `typeof window.postMessage = ${typeof window.postMessage}`,
                `history.length = ${history.length}`,
                `location.href = ${location.href}`,
            ];

            setCardResult(
                "window",
                "ok",
                "Le API base di window risultano disponibili. Questo non garantisce che il wrapper WebView consenta tutte le azioni."
            );
            lines.forEach(line => log("window", line));
        } catch (err) {
            setCardResult("window", "ko", "API base di window non disponibili o errore inatteso.");
            log("window", formatError(err));
        }
    }, [log, setCardResult]);

    const testPostMessage = useCallback(() => {
        try {
            const token = `pm_${Math.random().toString(16).slice(2)}`;

            const handler = (ev: MessageEvent) => {
                if ((ev.data as { token?: string })?.token === token) {
                    window.removeEventListener("message", handler);
                    setCardResult("window", "ok", "postMessage funziona su window.");
                    log("window", `Ricevuto postMessage con token ${token}`);
                }
            };

            window.addEventListener("message", handler);
            window.postMessage({token}, "*");
            log("window", `Inviato postMessage con token ${token}`);

            window.setTimeout(() => {
                window.removeEventListener("message", handler);
            }, 1000);
        } catch (err) {
            setCardResult("window", "ko", "postMessage ha fallito.");
            log("window", formatError(err));
        }
    }, [log, setCardResult]);

    const testWindowOpen = useCallback(() => {
        try {
            const win = window.open("https://example.com", "_blank", "noopener,noreferrer");

            if (!win) {
                setCardResult(
                    "window",
                    "warn",
                    "window.open sembra bloccato. In molte WebView è normale: il wrapper può intercettare o bloccare popup e nuove finestre."
                );
                log("window", "window.open ha restituito null/undefined.");
                return;
            }

            setCardResult("window", "ok", "window.open ha aperto una nuova finestra/tab.");
            log("window", "window.open riuscito.");
        } catch (err) {
            setCardResult(
                "window",
                "ko",
                "window.open ha lanciato un errore. Probabile blocco del wrapper o policy della WebView."
            );
            log("window", formatError(err));
        }
    }, [log, setCardResult]);

    const testExternalBlankLink = useCallback(() => {
        try {
            const a = document.createElement("a");
            a.href = "https://example.com";
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            setCardResult(
                "external-link",
                "warn",
                "Click su link esterno con target=_blank inviato. Se non si apre nulla, il wrapper potrebbe non gestire i link esterni o aprirli fuori dalla WebView."
            );
            log("external-link", "Click programmato su <a target=\"_blank\"> verso https://example.com");
        } catch (err) {
            setCardResult("external-link", "ko", "Errore durante l'apertura del link esterno.");
            log("external-link", formatError(err));
        }
    }, [log, setCardResult]);

    const testCookies = useCallback(() => {
        try {
            const key = "wv_test_cookie";
            const value = `v_${Date.now()}`;
            const securePart = location.protocol === "https:" ? "; Secure" : "";

            document.cookie = `${key}=${value}; path=/; SameSite=Lax${securePart}`;
            const ok = document.cookie.includes(`${key}=${value}`);

            if (ok) {
                setCardResult(
                    "cookies",
                    "ok",
                    "Cookie scritto e letto correttamente. Attenzione: in alcune WebView o su iOS embedded i cookie possono comunque avere limitazioni di persistenza."
                );
                log("cookies", `Cookie OK: ${key}=${value}`);
            } else {
                setCardResult(
                    "cookies",
                    "ko",
                    "Cookie non leggibile dopo la scrittura. Possibili cause: cookie disabilitati, policy WebView, contesto embedded, restrizioni iOS/ITP o problemi di SameSite/Secure."
                );
                log("cookies", `document.cookie attuale: ${document.cookie}`);
            }
        } catch (err) {
            setCardResult("cookies", "ko", "Eccezione durante test cookie.");
            log("cookies", formatError(err));
        }
    }, [log, setCardResult]);

    const testLocalStorage = useCallback(() => {
        try {
            const key = "wv_local_storage_test";
            const value = `ok_${Date.now()}`;
            localStorage.setItem(key, value);
            const read = localStorage.getItem(key);

            if (read === value) {
                setCardResult("storage", "ok", "localStorage funziona correttamente.");
                log("storage", `localStorage OK: ${key}=${read}`);
            } else {
                setCardResult("storage", "ko", "localStorage non restituisce il valore scritto.");
                log("storage", `localStorage valore letto: ${read}`);
            }
        } catch (err) {
            setCardResult(
                "storage",
                "ko",
                "Errore su localStorage. Possibili cause: storage disabilitato, modalità privata o policy WebView."
            );
            log("storage", `localStorage error: ${formatError(err)}`);
        }
    }, [log, setCardResult]);

    const testSessionStorage = useCallback(() => {
        try {
            const key = "wv_session_storage_test";
            const value = `ok_${Date.now()}`;
            sessionStorage.setItem(key, value);
            const read = sessionStorage.getItem(key);

            if (read === value) {
                setCardResult("storage", "ok", "sessionStorage funziona correttamente.");
                log("storage", `sessionStorage OK: ${key}=${read}`);
            } else {
                setCardResult("storage", "ko", "sessionStorage non restituisce il valore scritto.");
                log("storage", `sessionStorage valore letto: ${read}`);
            }
        } catch (err) {
            setCardResult(
                "storage",
                "ko",
                "Errore su sessionStorage. Possibili cause: storage disabilitato o policy WebView."
            );
            log("storage", `sessionStorage error: ${formatError(err)}`);
        }
    }, [log, setCardResult]);

    const testIndexedDb = useCallback(() => {
        try {
            if (!("indexedDB" in window)) {
                setCardResult("storage", "warn", "IndexedDB non disponibile in questa WebView/browser.");
                log("storage", "indexedDB non presente su window.");
                return;
            }

            const request = indexedDB.open("wv_idb_test", 1);

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains("kv")) {
                    db.createObjectStore("kv");
                }
            };

            request.onsuccess = () => {
                const db = request.result;
                const tx = db.transaction("kv", "readwrite");
                tx.objectStore("kv").put("hello", "k");

                tx.oncomplete = () => {
                    setCardResult("storage", "ok", "IndexedDB disponibile e scrivibile.");
                    log("storage", "IndexedDB open/write OK.");
                    db.close();
                };

                tx.onerror = () => {
                    setCardResult("storage", "ko", "IndexedDB disponibile ma transazione fallita.");
                    log("storage", "IndexedDB transaction error.");
                    db.close();
                };
            };

            request.onerror = () => {
                setCardResult(
                    "storage",
                    "ko",
                    "IndexedDB presente ma apertura fallita. Possibili cause: policy WebView, modalità privata, storage bloccato."
                );
                log("storage", `IndexedDB open error: ${request.error?.message ?? "unknown"}`);
            };
        } catch (err) {
            setCardResult("storage", "ko", "Errore inatteso durante test IndexedDB.");
            log("storage", formatError(err));
        }
    }, [log, setCardResult]);

    const testCacheApi = useCallback(async () => {
        try {
            if (!("caches" in window)) {
                setCardResult("cache", "warn", "Cache API non disponibile.");
                log("cache", "Cache API non disponibile.");
                return;
            }

            const cache = await caches.open("wv_cache_test");
            await cache.put(new Request("https://example.invalid/test"), new Response("ok"));
            const response = await cache.match("https://example.invalid/test");
            const text = response ? await response.text() : null;

            if (text === "ok") {
                setCardResult("cache", "ok", "Cache API disponibile e funzionante.");
                log("cache", "Cache API OK.");
            } else {
                setCardResult("cache", "ko", "Cache API presente ma lettura/scrittura non coerente.");
                log("cache", `Cache API mismatch: ${text}`);
            }
        } catch (err) {
            setCardResult(
                "cache",
                "ko",
                "Errore su Cache API. Possibili cause: secure context mancante o policy WebView."
            );
            log("cache", formatError(err));
        }
    }, [log, setCardResult]);

    const testServiceWorkerSupport = useCallback(() => {
        try {
            if ("serviceWorker" in navigator) {
                setCardResult("cache", "ok", "API Service Worker disponibile. Non implica che la registrazione sia consentita.");
                log("cache", "serviceWorker presente in navigator.");
            } else {
                setCardResult("cache", "warn", "Service Worker non supportato. In molte WebView è normale.");
                log("cache", "serviceWorker non supportato.");
            }
        } catch (err) {
            setCardResult("cache", "ko", "Errore durante verifica Service Worker.");
            log("cache", formatError(err));
        }
    }, [log, setCardResult]);

    const testClipboardWrite = useCallback(async () => {
        try {
            if (!navigator.clipboard?.writeText) {
                setCardResult("clipboard", "warn", "Clipboard writeText non disponibile.");
                log("clipboard", "navigator.clipboard.writeText non disponibile.");
                return;
            }

            const text = `WV_CLIP_${Date.now()}`;
            await navigator.clipboard.writeText(text);

            setCardResult("clipboard", "ok", `Test copy riuscito: "${text}"`);
            log("clipboard", `writeText OK: ${text}`);
        } catch (err) {
            setCardResult(
                "clipboard",
                "ko",
                "Clipboard write fallito. Possibili cause: secure context mancante, policy WebView o permessi."
            );
            log("clipboard", formatError(err));
        }
    }, [log, setCardResult]);

    const testClipboardRead = useCallback(async () => {
        try {
            if (!navigator.clipboard?.readText) {
                setCardResult("clipboard", "warn", "Clipboard readText non disponibile.");
                log("clipboard", "navigator.clipboard.readText non disponibile.");
                return;
            }

            const value = await navigator.clipboard.readText();
            setCardResult("clipboard", "ok", `Clipboard letto: "${(value || "").slice(0, 120)}"`);
            log("clipboard", `readText OK: ${value}`);
        } catch (err) {
            setCardResult(
                "clipboard",
                "ko",
                "Clipboard read fallito. Spesso richiede secure context e permessi espliciti."
            );
            log("clipboard", formatError(err));
        }
    }, [log, setCardResult]);

    const armFilePickerHint = useCallback(() => {
        if (filePickerTimerRef.current) {
            window.clearTimeout(filePickerTimerRef.current);
        }

        filePickerTimerRef.current = window.setTimeout(() => {
            setCardResult(
                "file",
                "warn",
                "Nessuna selezione file ricevuta. Possibili cause: utente ha chiuso il picker oppure la WebView non supporta il file chooser."
            );
            log("file", "Nessun change ricevuto dopo click su file input.");
        }, 1200);
    }, [log, setCardResult]);

    const openFilePicker = useCallback(() => {
        armFilePickerHint();
        fileInputRef.current?.click();
        log("file", "Tentata apertura file picker.");
    }, [armFilePickerHint, log]);

    const onFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (filePickerTimerRef.current) {
            window.clearTimeout(filePickerTimerRef.current);
        }

        const files = Array.from(e.target.files ?? []);
        setSelectedFiles(files);

        if (files.length > 0) {
            setCardResult(
                "file",
                "ok",
                "File picker funzionante: i file selezionati arrivano correttamente al browser."
            );
            log("file", `Ricevuti ${files.length} file: ${files.map(f => `${f.name} (${f.type || "?"}, ${f.size}b)`).join(", ")}`);
        } else {
            setCardResult(
                "file",
                "warn",
                "Evento change ricevuto ma nessun file presente. Possibile annullamento selezione."
            );
            log("file", "Change su input file senza file.");
        }
    }, [log, setCardResult]);

    const explainFilePickerKo = useCallback(() => {
        const message = [
            "Se in app non si apre il file picker e non vedi errori JS, molto spesso il problema è il wrapper WebView.",
            "",
            "Non esiste un permesso web standard per il file picker.",
            "Su Android WebView è tipico che manchi l'implementazione nativa del chooser (es. onShowFileChooser).",
            "Se la stessa pagina funziona in Safari/Chrome ma non dentro app, la causa più probabile è lato app.",
        ].join("\n");

        alert(message);
    }, []);

    const onDropZoneDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragActive(true);
    }, []);

    const onDropZoneDragLeave = useCallback(() => {
        setDragActive(false);
    }, []);

    const onDropZoneDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragActive(false);

        const files = Array.from(e.dataTransfer.files ?? []);
        setSelectedFiles(files);

        if (files.length > 0) {
            setCardResult("file", "ok", "Drag & drop funzionante: i file arrivano correttamente al DOM.");
            log("file", `Drop ricevuto con ${files.length} file.`);
        } else {
            setCardResult("file", "warn", "Drop eseguito ma nessun file disponibile in dataTransfer.");
            log("file", "Drop senza file.");
        }
    }, [log, setCardResult]);

    const testPermissionsApi = useCallback(async () => {
        try {
            if (!navigator.permissions?.query) {
                setCardResult(
                    "camera",
                    "warn",
                    "Permissions API non disponibile. In molte WebView è normale."
                );
                log("camera", "Permissions API non disponibile.");
                return;
            }

            const names = ["camera", "microphone", "clipboard-read", "clipboard-write"] as const;

            for (const name of names) {
                try {
                    const res = await navigator.permissions.query({name} as PermissionDescriptor);
                    log("camera", `Permission "${name}": ${res.state}`);
                } catch (err) {
                    log("camera", `Permission "${name}" non interrogabile: ${formatError(err)}`);
                }
            }

            setCardResult("camera", "ok", "Permissions API interrogata. Vedi log per i dettagli.");
        } catch (err) {
            setCardResult("camera", "ko", "Errore durante interrogazione Permissions API.");
            log("camera", formatError(err));
        }
    }, [log, setCardResult]);

    const testCamera = useCallback(async () => {
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                setCardResult("camera", "ko", "getUserMedia non disponibile.");
                log("camera", "navigator.mediaDevices.getUserMedia non disponibile.");
                return;
            }

            activeCameraStream = await navigator.mediaDevices.getUserMedia({
                video: {facingMode: "environment"},
                audio: false,
            });

            if (videoRef.current) {
                videoRef.current.srcObject = activeCameraStream;
            }

            setCardResult(
                "camera",
                "ok",
                "Camera avviata correttamente. Se qui fallisce con NotAllowedError, il problema è spesso un permesso negato o una policy del wrapper."
            );
            log("camera", "getUserMedia OK.");
        } catch (err) {
            setCardResult(
                "camera",
                "ko",
                "Camera non disponibile o permesso negato. Cause tipiche: NotAllowedError, SecurityError, assenza di secure context o policy WebView."
            );
            log("camera", formatError(err));
        }
    }, [log, setCardResult]);

    const stopCamera = useCallback(() => {
        if (activeCameraStream) {
            activeCameraStream.getTracks().forEach(track => track.stop());
            activeCameraStream = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        log("camera", "Camera stream fermato.");
    }, [log]);

    useEffect(() => {
        runEnvironmentTest();
        runWindowTest();

        const onFocus = () => log("file", "window focus ricevuto");
        const onVisibility = () => log("env", `visibilitychange: ${document.visibilityState}`);

        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);

            if (filePickerTimerRef.current) {
                window.clearTimeout(filePickerTimerRef.current);
            }

            if (activeCameraStream) {
                activeCameraStream.getTracks().forEach(track => track.stop());
                activeCameraStream = null;
            }
        };
    }, [log, runEnvironmentTest, runWindowTest]);

    return (
        <div style={{padding: 16, background: "#fff", color: "#111"}}>
            <h1 style={{fontSize: 22, margin: "0 0 8px"}}>WebView diagnostics</h1>

            <div style={{fontSize: 12, opacity: 0.8, marginBottom: 16}}>
                Questa pagina serve per verificare rapidamente quali funzionalità funzionano o no dentro WebView.
                <br/>
                User agent: {userAgent}
            </div>

            <div
                style={{
                    display: "grid",
                    gap: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                }}
            >
                <Card
                    title="1) Environment"
                    status={statuses.env ?? "idle"}
                    message={messages.env}
                    logs={logs.env}
                    actions={
                        <ActionButton onClick={runEnvironmentTest}>Riesegui</ActionButton>
                    }
                />

                <Card
                    title="2) Window / Messaging / Popups"
                    status={statuses.window ?? "idle"}
                    message={messages.window}
                    logs={logs.window}
                    actions={
                        <>
                            <ActionButton onClick={runWindowTest}>Test window</ActionButton>
                            <ActionButton onClick={testPostMessage}>Test postMessage</ActionButton>
                            <ActionButton onClick={testWindowOpen}>Test window.open</ActionButton>
                        </>
                    }
                />

                <Card
                    title="3) Link esterni (_blank)"
                    status={statuses["external-link"] ?? "idle"}
                    message={messages["external-link"]}
                    logs={logs["external-link"]}
                    actions={
                        <>
                            <ActionButton onClick={testExternalBlankLink}>Test link target=_blank</ActionButton>
                            <a
                                href="https://example.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    padding: "8px 10px",
                                    borderRadius: 10,
                                    border: "1px solid #ddd",
                                    background: "#f6f6f6",
                                    color: "inherit",
                                    textDecoration: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                }}
                                onClick={() => {
                                    setCardResult(
                                        "external-link",
                                        "warn",
                                        "Click manuale sul link inviato. Se non si apre nulla, il wrapper potrebbe bloccare i link esterni o non gestire _blank."
                                    );
                                    log("external-link", "Click manuale su anchor target=_blank");
                                }}
                            >
                                Link reale target=_blank
                            </a>
                        </>
                    }
                />

                <Card
                    title="4) Cookies"
                    status={statuses.cookies ?? "idle"}
                    message={messages.cookies}
                    logs={logs.cookies}
                    actions={
                        <ActionButton onClick={testCookies}>Set / Read cookie</ActionButton>
                    }
                />

                <Card
                    title="5) Storage"
                    status={statuses.storage ?? "idle"}
                    message={messages.storage}
                    logs={logs.storage}
                    actions={
                        <>
                            <ActionButton onClick={testLocalStorage}>Test localStorage</ActionButton>
                            <ActionButton onClick={testSessionStorage}>Test sessionStorage</ActionButton>
                            <ActionButton onClick={testIndexedDb}>Test IndexedDB</ActionButton>
                        </>
                    }
                />

                <Card
                    title="6) Cache / Service Worker"
                    status={statuses.cache ?? "idle"}
                    message={messages.cache}
                    logs={logs.cache}
                    actions={
                        <>
                            <ActionButton onClick={testCacheApi}>Test Cache API</ActionButton>
                            <ActionButton onClick={testServiceWorkerSupport}>Check Service Worker</ActionButton>
                        </>
                    }
                />

                <Card
                    title="7) Clipboard"
                    status={statuses.clipboard ?? "idle"}
                    message={messages.clipboard}
                    logs={logs.clipboard}
                    actions={
                        <>
                            <ActionButton onClick={testClipboardWrite}>Copy test</ActionButton>
                            <ActionButton onClick={testClipboardRead}>Read clipboard</ActionButton>
                        </>
                    }
                />

                <Card
                    title="8) File picker / Drag & Drop"
                    status={statuses.file ?? "idle"}
                    message={messages.file}
                    logs={logs.file}
                    actions={
                        <>
                            <ActionButton onClick={openFilePicker}>Apri file picker</ActionButton>
                            <ActionButton onClick={explainFilePickerKo}>Spiega KO</ActionButton>
                        </>
                    }
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        onChange={onFileInputChange}
                        style={{display: "none"}}
                    />

                    <div style={{fontSize: 12, marginBottom: 8}}>
                        Usa il bottone per aprire il picker. Se non succede nulla in app ma funziona nel browser,
                        il problema è probabilmente nel wrapper WebView.
                    </div>

                    <div
                        onDragOver={onDropZoneDragOver}
                        onDragLeave={onDropZoneDragLeave}
                        onDrop={onDropZoneDrop}
                        style={{
                            marginTop: 8,
                            border: "1px dashed #ccc",
                            borderRadius: 12,
                            padding: 16,
                            textAlign: "center",
                            background: dragActive ? "#fff" : "#fafafa",
                        }}
                    >
                        Drop zone
                    </div>

                    {selectedFiles.length > 0 && (
                        <div style={{marginTop: 10, fontSize: 12}}>
                            <b>File selezionati:</b>
                            <ul style={{paddingLeft: 18, margin: "6px 0 0"}}>
                                {selectedFiles.map(file => (
                                    <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                                        {file.name} — {file.type || "unknown"} — {file.size} bytes
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card>

                <Card
                    title="9) Camera / Permissions"
                    status={statuses.camera ?? "idle"}
                    message={messages.camera}
                    logs={logs.camera}
                    actions={
                        <>
                            <ActionButton onClick={testPermissionsApi}>Query Permissions API</ActionButton>
                            <ActionButton onClick={testCamera}>Test getUserMedia</ActionButton>
                            <ActionButton onClick={stopCamera}>Stop camera</ActionButton>
                        </>
                    }
                >
                    <video
                        ref={videoRef}
                        playsInline
                        autoPlay
                        muted
                        style={{
                            marginTop: 10,
                            width: "100%",
                            maxHeight: 240,
                            background: "#000",
                            borderRadius: 12,
                        }}
                    />
                </Card>
            </div>
        </div>
    );
}